// Admin audit log — local ring buffer (latest 500) mirrored to the backend.
// Faithfully reconstructed from the deployed admin chunk.

import { postAudit } from "../../lib/api";

const AUDIT_KEY = "kd_audit_v1";
const AUDIT_CHANGED = "kd-audit-changed";

export interface AuditEntry {
  id: string;
  timestamp: number;
  action: string;
  actor: string;
  target: string;
  detail?: string;
}

function load(): AuditEntry[] {
  try {
    const raw = localStorage.getItem(AUDIT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    /* ignore */
  }
  return [];
}

function save(list: AuditEntry[]): void {
  if (list.length > 500) list = list.slice(-500);
  try {
    localStorage.setItem(AUDIT_KEY, JSON.stringify(list));
    window.dispatchEvent(new Event(AUDIT_CHANGED));
  } catch {
    /* ignore */
  }
}

/** Record an admin action (local + best-effort backend). */
export function logAudit(action: string, target: string, detail?: string, actor = "admin"): void {
  const entry: AuditEntry = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
    timestamp: Date.now(),
    action,
    actor,
    target,
    detail,
  };
  save([...load(), entry]);
  postAudit(entry)?.catch?.(() => {});
}

/** Audit entries, newest first. */
export function getAuditLog(): AuditEntry[] {
  return [...load()].reverse();
}

export function clearAuditLog(): void {
  try {
    localStorage.removeItem(AUDIT_KEY);
    window.dispatchEvent(new Event(AUDIT_CHANGED));
  } catch {
    /* ignore */
  }
}

export function subscribeAudit(cb: () => void): () => void {
  window.addEventListener(AUDIT_CHANGED, cb);
  return () => window.removeEventListener(AUDIT_CHANGED, cb);
}

/** Export the audit log as CSV. */
export function exportAuditCSV(): string {
  const headers = ["Timestamp", "Actor", "Action", "Target", "Detail"];
  const rows = load().map((e) => [new Date(e.timestamp).toISOString(), e.actor, e.action, e.target, (e.detail || "").replace(/"/g, '""')]);
  return [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
}
